import React, { useEffect, useState, useRef } from "react";
import { Spin, Row, Col, Empty, Space, Button } from "antd";
import {
  TeamOutlined, DollarOutlined, WarningOutlined, InboxOutlined,
  NodeIndexOutlined, ReloadOutlined, ClockCircleOutlined, RiseOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { getDashboardOverview } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { faNum } from "../utils/faNumberFormat";

const MONTHS = ["فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور",
  "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند"];
const STATUS_LABELS = { open: "باز", calculated: "محاسبه‌شده", approved: "تایید‌شده", paid: "پرداخت‌شده" };
const fa = (n) => (n === null || n === undefined || n === "") ? "—" : faNum(Number(n));

// شمارشِ عددی از ۰ تا مقدار در ~۱ثانیه (easing 1-(1-p)^3)، خروجی fa-IR — با احترام به reduce-motion
function useCountUp(target) {
  const [val, setVal] = useState(0);
  const raf = useRef();
  useEffect(() => {
    const to = Number(target) || 0;
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setVal(to); return; }
    const dur = 1000, t0 = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      setVal(to * e);
      if (p < 1) raf.current = requestAnimationFrame(step); else setVal(to);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [target]);
  return Math.round(val);
}
function CountUp({ value }) {
  const v = useCountUp(value);
  return <>{faNum(v)}</>;
}

// کارتِ KPIِ بنتو با آیکون، شمارشِ عدد و ورودِ پلکانی
function KpiCard({ icon, tone, value, suffix, title, badge, onClick, delay = 0 }) {
  return (
    <div className="gx-sc-card gx-rise"
      style={{ height: "100%", cursor: onClick ? "pointer" : "default", padding: 18, animationDelay: `${delay}s` }}
      onClick={onClick}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <span style={{
          width: 42, height: 42, borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center",
          color: tone, background: `color-mix(in srgb, ${tone} 15%, transparent)`, fontSize: 21,
        }}>{icon}</span>
        {badge != null && <span style={{ fontSize: 11.5, fontWeight: 700, color: badge.color || "var(--text-sec)" }}>{badge.text}</span>}
      </div>
      <div style={{ fontSize: 26, fontWeight: 900, color: "var(--text)", lineHeight: 1.1, fontFeatureSettings: "'tnum'" }}>
        {typeof value === "number" ? <CountUp value={value} /> : value}
        {suffix && <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-sec)", marginInlineStart: 4 }}>{suffix}</span>}
      </div>
      <div style={{ fontSize: 12.5, color: "var(--text-sec)", marginTop: 6 }}>{title}</div>
    </div>
  );
}

// ردیفِ آیتمِ تولید داخلِ کارتِ hero
function ProdItem({ dot, name, badge, value, unit }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, background: "rgba(255,255,255,.1)",
      border: "1px solid rgba(255,255,255,.14)", borderRadius: 10, padding: "8px 12px",
    }}>
      <span style={{ width: 8, height: 8, borderRadius: 99, background: dot, flexShrink: 0 }} />
      <span style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>{name}</span>
      <span style={{
        fontSize: 10.5, fontWeight: 600, color: "#fff", background: "rgba(255,255,255,.16)",
        padding: "2px 8px", borderRadius: 6, marginInlineStart: "auto",
      }}>{badge}</span>
      <span style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,.92)", fontFeatureSettings: "'tnum'" }}>
        {fa(value)}{unit ? ` ${unit}` : ""}
      </span>
    </div>
  );
}

// دوناتِ وضعیتِ تولید — از فیلدهای موجودِ pr ساخته می‌شود
function StatusDonut({ segments }) {
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  const R = 52, C = 2 * Math.PI * R;
  let acc = 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16, margin: "auto 0" }}>
      <svg width="118" height="118" viewBox="0 0 128 128" style={{ flexShrink: 0, transform: "rotate(-90deg)" }}>
        <circle cx="64" cy="64" r={R} fill="none" stroke="var(--surface-2)" strokeWidth="17" />
        {segments.map((s, i) => {
          const frac = s.value / total;
          const dash = frac * C;
          const rot = (acc / total) * 360;
          acc += s.value;
          return (
            <circle key={i} cx="64" cy="64" r={R} fill="none" stroke={s.color} strokeWidth="17" strokeLinecap="round"
              strokeDasharray={`${dash} ${C}`} transform={`rotate(${rot} 64 64)`} />
          );
        })}
      </svg>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 9, fontSize: 12.5 }}>
        {segments.map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text-sec)" }}>
            <span style={{ width: 9, height: 9, borderRadius: 3, background: s.color }} />
            {s.label}
            <b style={{ color: "var(--text)", marginInlineStart: "auto", fontFeatureSettings: "'tnum'" }}>
              {fa(Math.round((s.value / total) * 100))}٪
            </b>
          </div>
        ))}
      </div>
    </div>
  );
}

// میلهٔ افقیِ زنجیرهٔ تأمین
function SupplyBar({ label, value, max, color, delay }) {
  const h = max > 0 ? Math.max(4, Math.round((value / max) * 100)) : 4;
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 8, height: "100%", justifyContent: "flex-end", minWidth: 0 }}>
      <b style={{ fontSize: 13, color: "var(--text)", fontFeatureSettings: "'tnum'" }}>{fa(value)}</b>
      <div className="gx-bar" style={{
        width: "100%", maxWidth: 74, height: `${h}%`, borderRadius: "9px 9px 0 0",
        background: `linear-gradient(180deg, color-mix(in srgb, ${color} 78%, #fff), ${color})`, animationDelay: `${delay}s`,
      }} />
      <span style={{ fontSize: 12, color: "var(--text-sec)", textAlign: "center" }}>{label}</span>
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, canView, canViewAmounts } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    getDashboardOverview().then((r) => setData(r.data)).catch(() => setData({ sections: [] })).finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (!data) return <Spin spinning={loading}><div style={{ minHeight: 240 }} /></Spin>;

  const sc = data.supply_chain, pr = data.production, pay = data.payroll, al = data.alerts || {};
  const moneySC = canViewAmounts("supply_chain"), moneyPay = canViewAmounts("payroll");
  const alertTotal = (al.sc_draft || 0) + (al.sc_disputed || 0) + (al.sc_needs_review || 0) + (al.payroll_negative || 0);

  const canSC = canView("supply_chain") && sc;
  const canProd = canView("production") && pr;
  const canPay = canView("payroll") && pay;

  // بخش‌های دوناتِ وضعیتِ تولید (فقط از فیلدهای موجود)
  const donutSegs = canProd ? [
    { label: "در تولید", value: pr.in_production || 0, color: "var(--stage-yarn)" },
    { label: "کنترل کیفیت", value: pr.pending_qc || 0, color: "var(--info)" },
    { label: "آمادهٔ بسته‌بندی", value: pr.ready_to_pack || 0, color: "var(--gold)" },
    { label: "بسته‌شده", value: pr.closed || 0, color: "var(--success)" },
  ] : [];
  const donutHasData = donutSegs.some((s) => s.value > 0);

  // بیشینهٔ میله‌های زنجیره برای مقیاسِ نسبی
  const scMax = canSC ? Math.max(
    sc.stage1?.yarn_total || 0, sc.stage3?.raw_input_kg || 0,
    sc.stage3?.dyed_output_kg || 0, Math.abs(sc.stage3?.balance_kg || 0), 1
  ) : 1;

  return (
    <div className="gx-page">
      {/* سربرگ + اقدامات */}
      <div className="gx-page-head gx-rise" style={{ animationDelay: "0s" }}>
        <div>
          <h2 className="gx-h1">داشبوردِ مدیریت — قرچک فکتوری</h2>
          <div className="gx-sub">
            خوش آمدید، {user?.full_name}
            {canPay && pay.period_month ? ` · دورهٔ ${MONTHS[pay.period_month - 1]} ${fa(pay.period_year)}` : ""}
          </div>
        </div>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>تازه‌سازی</Button>
      </div>

      {/* هشدارها */}
      {alertTotal > 0 && (
        <div className="gx-rise" style={{
          display: "flex", alignItems: "center", gap: 14, padding: "13px 16px", borderRadius: 14,
          background: "color-mix(in srgb, var(--gold) 10%, transparent)",
          border: "1px solid color-mix(in srgb, var(--gold) 32%, transparent)", animationDelay: "0.04s",
        }}>
          <WarningOutlined style={{ color: "var(--gold)", fontSize: 20, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "var(--text)" }}>{fa(alertTotal)} موردِ نیازمندِ بررسی</div>
            <Space wrap style={{ marginTop: 6 }}>
              {al.sc_needs_review > 0 && <Chip tone="gold">زنجیره نیازمند بررسی: {fa(al.sc_needs_review)}</Chip>}
              {al.sc_disputed > 0 && <Chip tone="danger">مغایرتِ زنجیره: {fa(al.sc_disputed)}</Chip>}
              {al.sc_draft > 0 && <Chip tone="mute">پیش‌نویسِ زنجیره: {fa(al.sc_draft)}</Chip>}
              {al.payroll_negative > 0 && <Chip tone="danger">حقوقِ منفی: {fa(al.payroll_negative)}</Chip>}
            </Space>
          </div>
        </div>
      )}

      {/* ردیفِ hero: تولید + دوناتِ وضعیت */}
      {canProd && (
        <Row gutter={[16, 16]} align="stretch">
          <Col xs={24} lg={15}>
            <div className="gx-rise" style={{
              height: "100%", borderRadius: 16, padding: 22, position: "relative", overflow: "hidden",
              background: "linear-gradient(150deg, var(--brand), var(--brand-strong))",
              boxShadow: "0 12px 30px color-mix(in srgb, var(--brand) 28%, transparent)",
              display: "flex", flexDirection: "column", cursor: "pointer", animationDelay: "0.08s",
            }} onClick={() => navigate("/production")}>
              <div style={{ position: "absolute", top: -40, insetInlineStart: -40, width: 180, height: 180, borderRadius: 99, background: "rgba(255,255,255,.06)" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", position: "relative", marginBottom: 16 }}>
                <div>
                  <div style={{ color: "rgba(255,255,255,.75)", fontSize: 13.5 }}>سفارش‌های فعالِ تولید</div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 8 }}>
                    <span style={{ fontSize: 46, fontWeight: 900, color: "#fff", lineHeight: 1, fontFeatureSettings: "'tnum'" }}>
                      <CountUp value={pr.active_orders || 0} />
                    </span>
                    <span style={{ fontSize: 16, color: "rgba(255,255,255,.8)", fontWeight: 600 }}>سری در جریان</span>
                  </div>
                  {(pr.in_production || 0) > 0 && (
                    <div style={{
                      display: "inline-flex", alignItems: "center", gap: 6, marginTop: 12, background: "rgba(255,255,255,.16)",
                      color: "#fff", fontSize: 12.5, fontWeight: 700, padding: "4px 11px", borderRadius: 99,
                    }}>
                      <span style={{ width: 7, height: 7, borderRadius: 99, background: "#7bde9e" }} />
                      {fa(pr.in_production)} سری در حالِ تولید فعال
                    </div>
                  )}
                </div>
                <div style={{ width: 50, height: 50, borderRadius: 14, background: "rgba(255,255,255,.16)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>
                  <InboxOutlined />
                </div>
              </div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,.7)", marginBottom: 8, position: "relative" }}>وضعیتِ مراحلِ تولید</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 7, position: "relative" }}>
                <ProdItem dot="#a9bcff" name="در حالِ تولید" badge="در تولید" value={pr.in_production} unit="سری" />
                <ProdItem dot="#8fdcff" name="در انتظارِ کنترل کیفیت" badge="کنترل کیفیت" value={pr.pending_qc} unit="سری" />
                <ProdItem dot="#f0cf8a" name="آمادهٔ بسته‌بندی" badge="بسته‌بندی" value={pr.ready_to_pack} unit="سری" />
                <ProdItem dot="#7bde9e" name="بسته‌شده" badge="تکمیل" value={pr.closed} unit="سری" />
              </div>
            </div>
          </Col>
          <Col xs={24} lg={9}>
            <div className="gx-sc-card gx-rise" style={{ height: "100%", padding: 18, display: "flex", flexDirection: "column", animationDelay: "0.14s" }}>
              <b style={{ fontSize: 15, color: "var(--text)" }}>وضعیتِ تولید</b>
              {donutHasData
                ? <StatusDonut segments={donutSegs} />
                : <div style={{ margin: "auto 0" }}><Empty description="داده‌ای نیست" image={Empty.PRESENTED_IMAGE_SIMPLE} /></div>}
            </div>
          </Col>
        </Row>
      )}

      {/* KPIهای تولید (اگر hero نبود) — نمای فشرده */}
      {canProd && (
        <Row gutter={[16, 16]}>
          <Col xs={12} md={6}>
            <KpiCard icon={<InboxOutlined />} tone="var(--brand-soft)" value={pr.active_orders || 0} title="سفارش‌های فعال"
              onClick={() => navigate("/orders")} delay={0.18} />
          </Col>
          <Col xs={12} md={6}>
            <KpiCard icon={<InboxOutlined />} tone="var(--info)" value={pr.today_operations || 0} suffix="عملیات"
              title="عملیاتِ امروز" badge={{ text: "امروز", color: "var(--gold)" }} delay={0.24} />
          </Col>
          <Col xs={12} md={6}>
            <KpiCard icon={<WarningOutlined />} tone="var(--gold)" value={pr.pending_qc || 0} title="در انتظارِ کنترل کیفیت"
              onClick={() => navigate("/production")} delay={0.3} />
          </Col>
          <Col xs={12} md={6}>
            <KpiCard icon={<RiseOutlined />} tone="var(--success)" value={pr.closed || 0} title="سری‌های بسته‌شده"
              onClick={() => navigate("/production")} delay={0.36} />
          </Col>
        </Row>
      )}

      {/* KPIهای حقوق و حسابداری */}
      {canPay && (
        <Row gutter={[16, 16]}>
          <Col xs={12} md={6}>
            <KpiCard icon={<TeamOutlined />} tone="var(--brand-soft)" value={pay.active_personnel || 0} suffix="نفر"
              title="پرسنلِ فعال" onClick={() => navigate("/employees")} delay={0.18} />
          </Col>
          {moneyPay ? (
            <Col xs={12} md={6}>
              <KpiCard icon={<DollarOutlined />} tone="var(--success)" value={Math.round(pay.net_total || 0)} suffix="﷼"
                title="جمعِ حقوقِ خالص" onClick={() => navigate("/payroll")} delay={0.24} />
            </Col>
          ) : (
            <Col xs={12} md={6}>
              <KpiCard icon={<DollarOutlined />} tone="var(--success)"
                value={pay.period_status ? (STATUS_LABELS[pay.period_status] || pay.period_status) : "—"}
                title="وضعیتِ دورهٔ جاری" onClick={() => navigate("/payroll")} delay={0.24} />
            </Col>
          )}
          {moneyPay && (
            <Col xs={12} md={6}>
              <KpiCard icon={<DollarOutlined />} tone="var(--info)" value={Math.round(pay.advances_total || 0)} suffix="﷼"
                title="جمعِ مساعده‌ها" onClick={() => navigate("/deductions")} delay={0.3} />
            </Col>
          )}
          <Col xs={12} md={6}>
            <KpiCard icon={<ClockCircleOutlined />} tone={(pay.unapproved || 0) > 0 ? "var(--gold)" : "var(--text-sec)"}
              value={pay.unapproved || 0} title="فیش‌های تأییدنشده" onClick={() => navigate("/payroll")} delay={0.36} />
          </Col>
          {(pay.negative_warnings || 0) > 0 && (
            <Col xs={12} md={6}>
              <KpiCard icon={<WarningOutlined />} tone="var(--danger)" value={pay.negative_warnings}
                title="هشدارِ حقوقِ منفی" onClick={() => navigate("/payroll")} delay={0.42} />
            </Col>
          )}
        </Row>
      )}

      {/* جریانِ زنجیرهٔ تأمین — میله‌های افقی */}
      {canSC && (
        <div className="gx-sc-card gx-rise" style={{ animationDelay: "0.42s" }}>
          <div className="gx-sc-card-head">
            <b className="gx-sc-card-title">
              <NodeIndexOutlined style={{ color: "var(--stage-dyed)" }} />
              جریانِ زنجیرهٔ تأمین (کیلوگرم)
            </b>
            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate("/supply-chain")}>مشاهدهٔ کامل ›</Button>
          </div>
          <div style={{ padding: "22px 20px", display: "flex", alignItems: "flex-end", gap: 24, height: 190 }}>
            <SupplyBar label="نخِ خریداری‌شده" value={sc.stage1?.yarn_total || 0} max={scMax} color="var(--stage-yarn)" delay={0.5} />
            <SupplyBar label="ورودیِ خامِ پوشان" value={sc.stage3?.raw_input_kg || 0} max={scMax} color="var(--stage-raw)" delay={0.62} />
            <SupplyBar label="خروجیِ رنگ‌شده" value={sc.stage3?.dyed_output_kg || 0} max={scMax} color="var(--stage-received)" delay={0.74} />
            <SupplyBar label="ماندهٔ نزدِ پوشان" value={sc.stage3?.balance_kg || 0} max={scMax}
              color={(sc.stage3?.balance_kg || 0) < 0 ? "var(--danger)" : "var(--gold)"} delay={0.86} />
          </div>
          {moneySC && (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "13px 18px", borderTop: "1px solid var(--border)", background: "var(--surface-2)",
            }}>
              <span style={{ fontSize: 13, color: "var(--text-sec)" }}>مبلغِ خرید نخ</span>
              <b style={{ fontSize: 15, color: "var(--gold)", fontFeatureSettings: "'tnum'" }}>{fa(Math.round(sc.stage1?.amount || 0))} ﷼</b>
            </div>
          )}
        </div>
      )}

      {!canView("supply_chain") && !canView("production") && !canView("payroll") && (
        <Empty description="برای نمایشِ داشبورد، دسترسیِ لازم را ندارید" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
    </div>
  );
}

// تگِ رنگیِ کوچک برای هشدارها (بدونِ hex پراکنده — از توکن‌ها)
function Chip({ tone = "mute", children }) {
  const map = {
    danger: { c: "var(--danger)", bg: "color-mix(in srgb, var(--danger) 14%, transparent)" },
    gold: { c: "var(--gold)", bg: "color-mix(in srgb, var(--gold) 14%, transparent)" },
    mute: { c: "var(--text-sec)", bg: "var(--surface-2)" },
  };
  const t = map[tone] || map.mute;
  return (
    <span style={{ fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 7, background: t.bg, color: t.c }}>
      {children}
    </span>
  );
}
